import * as cookie from 'cookie';
import { redirect } from 'react-router';

import { apiIsomorphicFetchUserEnvironment } from '#/api/userEnvironment/apiFetchUserEnvironment';
import { configString } from '#/config/appConfigUtils';
import type { HorizonAppLoaderContext } from '#/horizon/types/horizon-app-loader-context';
import { HORIZON_ENABLED } from '#/rendering/state/renderingState';
import { analyticsQueryParamKeys } from '#/utils/analytics/setup/configureAnalyticsUserData';
import { isExternalURL } from '#/utils/url/isExternalURL';
import { getFlashMessageId } from '#/zustand-stores/flashMessagesStore';

type Args = {
  request: Request;
  context: HorizonAppLoaderContext;
};

type Options = {
  flashMessage?: Omit<FlashMessageType, 'id'>;
  status?: number;
};

export function idealistRedirect({ request, context }: Args) {
  return async (url: string, options?: Options) => {
    const appUrl: string = await (async () => {
      if (!HORIZON_ENABLED) return configString('idealist', 'appUrl');

      const { appConfig } = await apiIsomorphicFetchUserEnvironment({
        request,
        context,
      })();

      return appConfig.idealist.appUrl;
    })();

    if (isExternalURL({ url, appUrl })) return redirect(url);

    const headers: Record<string, string> = {};

    // horizon-flash-message is only consumed on horizon
    //
    // We don't use the regular flashMessages for horizon
    // because it causes hydration errors, and it relies on
    // atob which has some strange bugs such as crashing when
    // serializing the JSON for `{ type: 'FORBIDDEN' }` flash message
    if (HORIZON_ENABLED && options?.flashMessage) {
      const twoDaysFromNow = new Date();
      twoDaysFromNow.setDate(twoDaysFromNow.getDate() + 2);

      headers['Set-Cookie'] = cookie.serialize(
        'horizon-flash-message',
        JSON.stringify({ ...options.flashMessage, id: getFlashMessageId() }),
        { path: '/', expires: twoDaysFromNow },
      );
    }

    const currentUrl = new URL(request.url);
    const newUrl = new URL(url, currentUrl.origin);

    for (const param of analyticsQueryParamKeys) {
      if (currentUrl.searchParams.has(param))
        newUrl.searchParams.set(
          param,
          currentUrl.searchParams.get(param) ?? '',
        );
    }

    return redirect(newUrl.toString(), { headers, status: options?.status });
  };
}
